import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useContext} from 'react';
import {useIntl} from 'react-intl';
import useAlerts from 'src/hooks/use-alerts';
import MaintenanceService, { } from 'src/services/maintenance-service';
import {MaintenanceDispatchContext} from './contexts';
import MaintenanceModel from 'src/core/models/maintenanceModel';
import Maintenance from 'src/core/models/maintenance';
import {kmToM, mToKm} from 'src/helpers/conversions';
import type {CompleteMaintenanceDto, CreateMaintenance, UpdateMaintenanceDto, UpdateRealizedMaintenanceDto} from './dto';

export const maintenanceKeys = {
  all: ['maintenance'],
  realized: () => [...maintenanceKeys.all, 'realized'],
  realizedByStatus: (status: 'pending' | 'inProgress' | 'completed') => [...maintenanceKeys.realized(), {status}],
};

export const mapMaintenanceOdometerToKm = (maintenance: Maintenance): Maintenance => ({
  ...maintenance,
  odometerValue: mToKm(maintenance.odometerValue),
});


export const mapMaintenanceModelOdometerToKm = (maintenance: MaintenanceModel): MaintenanceModel => ({
  ...maintenance,
  nextOdometer: maintenance.nextOdometer === undefined ? undefined : mToKm(maintenance.nextOdometer),
  lastOdometer: maintenance.lastOdometer === undefined ? undefined : mToKm(maintenance.lastOdometer),
  currentOdometer: maintenance.currentOdometer === undefined ? undefined : mToKm(maintenance.currentOdometer),
});

export const toRealizedPayload = <T extends {nextOdometer?: number}>(data: T): T => ({
  ...data,
  nextOdometer: data.nextOdometer === undefined ? undefined : kmToM(data.nextOdometer),
});

export const useMaintenances = () => {
  const maintenanceService = new MaintenanceService();

  return useQuery<unknown, unknown, Maintenance[]>({
    queryKey: maintenanceKeys.all,
    queryFn: () => maintenanceService.list().then((maintenances: Maintenance[]) => maintenances.map(mapMaintenanceOdometerToKm)),
    initialData: [],
  });
};

export const useUpdateMaintenance = () => {
  const queryClient = useQueryClient();
  const {toast} = useAlerts() as unknown as {toast: Record<string, (text: string) => void>};
  const maintenanceService = new MaintenanceService();
  const intl = useIntl();

  return useMutation<unknown, unknown, UpdateMaintenanceDto>({
    mutationFn: (data) => maintenanceService.update({...data, odometerValue: kmToM(data.odometerValue)}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: maintenanceKeys.all});
      toast.success(intl.formatMessage({id: 'maintenanceEditedSuccess'}));
    },
  });
};

export const useDeleteMaintenance = () => {
  const queryClient = useQueryClient();
  const {toast} = useAlerts() as unknown as {toast: Record<string, (text: string) => void>};
  const maintenanceService = new MaintenanceService();
  const intl = useIntl();

  return useMutation<unknown, unknown, number>({
    mutationFn: (data) => maintenanceService.remove(data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: maintenanceKeys.all});
      toast.success(intl.formatMessage({id: 'maintenanceRemovedSuccess'}));
    },
  });
};

export const useMaintenancesRealizedByStatus = (status: 'pending' | 'inProgress' | 'completed') => {
  const maintenanceService = new MaintenanceService();

  return useQuery<unknown, unknown, MaintenanceModel[]>({
    queryKey: maintenanceKeys.realizedByStatus(status),
    queryFn: () => maintenanceService.listRealizedByStatus(status).then((maintenances: MaintenanceModel[]) => maintenances.map(mapMaintenanceModelOdometerToKm)),
    initialData: [],
  });
};

export const useCreateMaintenance = () => {
  const queryClient = useQueryClient();
  const maintenanceService = new MaintenanceService();
  const {toast} = useAlerts() as unknown as {toast: Record<string, (text: string) => void>};
  const maintenanceDispatch = useContext(MaintenanceDispatchContext);
  const intl = useIntl();

  return useMutation<unknown, unknown, CreateMaintenance>({
    mutationFn: (data) => maintenanceService.save({
      ...data,
      odometerValue: data.odometerValue === undefined ? undefined : kmToM(data.odometerValue),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: maintenanceKeys.all});
      maintenanceDispatch({type: 'SET_ACTIVE_VIEW', payload: 'list'});
      toast.success(intl.formatMessage({id: 'maintenanceCreatedSuccess'}));
    },
  });
};

export const useRealizeMaintenance = () => {
  const queryClient = useQueryClient();
  const maintenanceService = new MaintenanceService();
  const {toast} = useAlerts() as unknown as {toast: Record<string, (text: string) => void>};
  const intl = useIntl();

  return useMutation<unknown, unknown, UpdateRealizedMaintenanceDto>({
    mutationFn: (data) => maintenanceService.updateRealized(toRealizedPayload(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: maintenanceKeys.realized()});
      toast.success(intl.formatMessage({id: 'maintenanceRealizedSuccess'}));
    },
  });
};

export const useRemoveInProgressMaintenance = () => {
  const queryClient = useQueryClient();
  const maintenanceService = new MaintenanceService();
  const {toast} = useAlerts() as unknown as {toast: Record<string, (text: string) => void>};
  const intl = useIntl();

  return useMutation<unknown, unknown, MaintenanceModel>({
    mutationFn: (data) => maintenanceService.updateRealized({
      ...data,
      status: 'pending',
      cost: undefined,
      description: undefined,
      vehicle: undefined,
      title: undefined,
      currentDate: undefined,
      currentHourmeter: undefined,
      currentOdometer: undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: maintenanceKeys.realized()});
      toast.success(intl.formatMessage({id: 'maintenanceRemovedSuccess'}));
    },
  });
};

export const useCompleteMaintenance = () => {
  const queryClient = useQueryClient();
  const maintenanceService = new MaintenanceService();
  const {toast} = useAlerts() as unknown as {toast: Record<string, (text: string) => void>};
  const intl = useIntl();

  return useMutation<unknown, unknown, CompleteMaintenanceDto>({
    mutationFn: (data) => maintenanceService.completeRealized(toRealizedPayload(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: maintenanceKeys.realized()});
      toast.success(intl.formatMessage({id: 'maintenanceFinishedSuccess'}));
    },
  });
};

